<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import {
  useOrganizationsStore,
  type Repository,
  type BranchInfo,
  type TagInfo,
  type PullRequestInfo,
} from '../stores/organizations';
import type { UIRefType } from '../types';
import { getProviderVocabulary } from '@kilnci/shared';

const props = defineProps<{
  modelValue: string;
  repository: Repository | null;
  placeholder?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'update:refType': [type: UIRefType | null];
}>();

type RefItem = {
  type: UIRefType;
  name: string;
  label: string;
  isDefault?: boolean;
  prNumber?: number;
  prTitle?: string;
};

const orgsStore = useOrganizationsStore();

const inputValue = ref(props.modelValue);
const isOpen = ref(false);
const activeIndex = ref(-1);

const branches = ref<BranchInfo[]>([]);
const tags = ref<TagInfo[]>([]);
const pullRequests = ref<PullRequestInfo[]>([]);

const loading = ref(false);
const loaded = ref(false);

const containerRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const listRef = ref<HTMLElement | null>(null);

// Get org slug from the store's organizations list
const orgSlug = computed(() => {
  if (!props.repository) return null;
  const org = orgsStore.organizations.find(
    (o) => o.id === props.repository!.organizationId
  );
  return org?.slug ?? null;
});

const providerVocab = computed(() => {
  if (!props.repository) return null;
  return getProviderVocabulary(props.repository.provider);
});

// Combine all refs into a single list
const allRefs = computed<RefItem[]>(() => {
  const items: RefItem[] = [];

  // Add branches (default first, then alphabetically)
  const sortedBranches = [...branches.value].sort((a, b) => {
    if (a.default && !b.default) return -1;
    if (!a.default && b.default) return 1;
    return a.name.localeCompare(b.name);
  });

  for (const branch of sortedBranches) {
    items.push({
      type: 'branch',
      name: branch.name,
      label: branch.name,
      isDefault: branch.default,
    });
  }

  // Add tags (newest first based on commit date)
  const sortedTags = [...tags.value].sort((a, b) => {
    return new Date(b.commit.date).getTime() - new Date(a.commit.date).getTime();
  });

  for (const tag of sortedTags) {
    items.push({
      type: 'tag',
      name: tag.name,
      label: tag.name,
    });
  }

  // Add pull requests (open ones, sorted by number)
  const sortedPRs = [...pullRequests.value].sort((a, b) => b.number - a.number);

  for (const pr of sortedPRs) {
    items.push({
      type: 'pr',
      name: pr.sourceBranch,
      label: `#${pr.number}: ${pr.title}`,
      prNumber: pr.number,
      prTitle: pr.title,
    });
  }

  return items;
});

// Filter refs based on input
const filteredRefs = computed(() => {
  const query = inputValue.value.toLowerCase().trim();
  if (!query) return allRefs.value;

  return allRefs.value.filter((item) => {
    return (
      item.name.toLowerCase().includes(query) ||
      item.label.toLowerCase().includes(query)
    );
  });
});

// Group filtered refs by type
const groupedRefs = computed(() => {
  const groups: { type: UIRefType; label: string; items: RefItem[] }[] = [];

  const branchItems = filteredRefs.value.filter((r) => r.type === 'branch');
  const tagItems = filteredRefs.value.filter((r) => r.type === 'tag');
  const prItems = filteredRefs.value.filter((r) => r.type === 'pr');

  if (branchItems.length > 0) {
    groups.push({ type: 'branch', label: 'Branches', items: branchItems });
  }
  if (tagItems.length > 0) {
    groups.push({ type: 'tag', label: 'Tags', items: tagItems });
  }
  if (prItems.length > 0) {
    const prLabel = providerVocab.value?.pullRequests ?? 'Pull Requests';
    groups.push({ type: 'pr', label: prLabel, items: prItems });
  }

  return groups;
});

// Flat list for keyboard navigation
const flatFilteredRefs = computed(() => filteredRefs.value);

async function loadRefs() {
  if (!props.repository || !orgSlug.value || loaded.value) return;

  loading.value = true;
  try {
    const [branchResult, tagResult, prResult] = await Promise.all([
      orgsStore.fetchBranches(orgSlug.value, props.repository.id),
      orgsStore.fetchTags(orgSlug.value, props.repository.id),
      orgsStore.fetchPullRequests(orgSlug.value, props.repository.id, 'open'),
    ]);

    branches.value = branchResult;
    tags.value = tagResult;
    pullRequests.value = prResult;
    loaded.value = true;
  } finally {
    loading.value = false;
  }
}

function openDropdown() {
  if (!props.repository) return;
  isOpen.value = true;
  activeIndex.value = -1;
  loadRefs();
}

function closeDropdown() {
  isOpen.value = false;
  activeIndex.value = -1;
}

function selectItem(item: RefItem) {
  inputValue.value = item.name;
  emit('update:modelValue', item.name);
  emit('update:refType', item.type);
  closeDropdown();
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  emit('update:modelValue', target.value);
  // When typing manually, we don't know the ref type until user selects from dropdown
  emit('update:refType', null);

  if (!isOpen.value && props.repository) {
    openDropdown();
  }
}

function handleFocus() {
  if (props.repository) {
    openDropdown();
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!isOpen.value) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      openDropdown();
      event.preventDefault();
    }
    return;
  }

  const items = flatFilteredRefs.value;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      activeIndex.value = Math.min(activeIndex.value + 1, items.length - 1);
      scrollToActive();
      break;
    case 'ArrowUp':
      event.preventDefault();
      activeIndex.value = Math.max(activeIndex.value - 1, -1);
      scrollToActive();
      break;
    case 'Enter':
      event.preventDefault();
      if (activeIndex.value >= 0 && activeIndex.value < items.length) {
        selectItem(items[activeIndex.value]);
      } else {
        closeDropdown();
      }
      break;
    case 'Escape':
      event.preventDefault();
      closeDropdown();
      break;
    case 'Tab':
      closeDropdown();
      break;
  }
}

function scrollToActive() {
  if (activeIndex.value < 0 || !listRef.value) return;

  const items = listRef.value.querySelectorAll('.ref-item');
  if (items[activeIndex.value]) {
    items[activeIndex.value].scrollIntoView({ block: 'nearest' });
  }
}

function handleClickOutside(event: MouseEvent) {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
}

// Reset when repository changes
watch(
  () => props.repository?.id,
  () => {
    loaded.value = false;
    branches.value = [];
    tags.value = [];
    pullRequests.value = [];
    // Keep the input value if switching repos with same branch name typed
  }
);

// Sync with external modelValue changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== inputValue.value) {
      inputValue.value = newValue;
    }
  }
);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div ref="containerRef" class="ref-selector">
    <div class="input-wrapper">
      <input
        ref="inputRef"
        :value="inputValue"
        type="text"
        :placeholder="placeholder"
        :disabled="!repository"
        @input="handleInput"
        @focus="handleFocus"
        @keydown="handleKeydown"
      />
      <button
        v-if="repository"
        type="button"
        class="dropdown-toggle"
        tabindex="-1"
        @click="isOpen ? closeDropdown() : openDropdown()"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          :class="{ rotated: isOpen }"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div v-if="isOpen" ref="listRef" class="dropdown">
      <div v-if="loading" class="dropdown-loading">
        <div class="spinner"></div>
        <span>Loading...</span>
      </div>

      <template v-else-if="groupedRefs.length > 0">
        <div v-for="group in groupedRefs" :key="group.type" class="ref-group">
          <div class="ref-group-header">{{ group.label }}</div>
          <div
            v-for="item in group.items"
            :key="`${item.type}-${item.name}`"
            class="ref-item"
            :class="{
              active: flatFilteredRefs.indexOf(item) === activeIndex,
            }"
            @click="selectItem(item)"
            @mouseenter="activeIndex = flatFilteredRefs.indexOf(item)"
          >
            <span class="ref-icon">
              <svg v-if="item.type === 'branch'" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11.75 2.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zm-2.25.75a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.492 2.492 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25zM4.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zM3.5 3.25a.75.75 0 1 1 1.5 0 .75.75 0 0 1-1.5 0z"/>
              </svg>
              <svg v-else-if="item.type === 'tag'" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2.5 7.775V2.75a.25.25 0 0 1 .25-.25h5.025a.25.25 0 0 1 .177.073l6.25 6.25a.25.25 0 0 1 0 .354l-5.025 5.025a.25.25 0 0 1-.354 0l-6.25-6.25a.25.25 0 0 1-.073-.177zm-1.5 0V2.75C1 1.784 1.784 1 2.75 1h5.025c.464 0 .91.184 1.238.513l6.25 6.25a1.75 1.75 0 0 1 0 2.474l-5.026 5.026a1.75 1.75 0 0 1-2.474 0l-6.25-6.25A1.75 1.75 0 0 1 1 7.775zM6 5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
              <svg v-else width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1.5 3.25a2.25 2.25 0 1 1 3 2.122v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.25 2.25 0 0 1 1.5 3.25zm5.677-.177L9.573.677A.25.25 0 0 1 10 .854V2.5h1A2.5 2.5 0 0 1 13.5 5v5.628a2.251 2.251 0 1 1-1.5 0V5a1 1 0 0 0-1-1h-1v1.646a.25.25 0 0 1-.427.177L7.177 3.427a.25.25 0 0 1 0-.354zM3.75 2.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zm0 9.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zm8.25.75a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0z"/>
              </svg>
            </span>
            <span class="ref-name">{{ item.name }}</span>
            <span v-if="item.isDefault" class="ref-badge default">default</span>
            <span v-if="item.type === 'pr'" class="ref-pr-info">#{{ item.prNumber }}</span>
          </div>
        </div>
      </template>

      <div v-else class="dropdown-empty">
        <span v-if="inputValue.trim()">No matches found</span>
        <span v-else>No refs available</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ref-selector {
  position: relative;
  width: 100%;
}

.input-wrapper {
  position: relative;
  display: flex;
}

.input-wrapper input {
  flex: 1;
  padding: 10px 36px 10px 12px;
  border: 1px solid var(--color-input-border);
  border-radius: 6px;
  font-size: 0.95rem;
  font-family: inherit;
  background: var(--color-input-bg);
  color: var(--color-input-text);
  width: 100%;
}

.input-wrapper input:focus {
  outline: none;
  border-color: var(--color-input-focus-border);
  box-shadow: 0 0 0 3px var(--color-input-focus-ring);
}

.input-wrapper input:disabled {
  background-color: var(--color-bg-tertiary);
  cursor: not-allowed;
}

.dropdown-toggle {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.dropdown-toggle:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

.dropdown-toggle svg {
  transition: transform 0.2s;
}

.dropdown-toggle svg.rotated {
  transform: rotate(180deg);
}

.dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--color-card-bg);
  border: 1px solid var(--color-card-border);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--color-card-shadow);
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
}

.dropdown-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  color: var(--color-text-secondary);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-border-secondary);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.dropdown-empty {
  padding: 16px;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.ref-group {
  border-bottom: 1px solid var(--color-border-secondary);
}

.ref-group:last-child {
  border-bottom: none;
}

.ref-group-header {
  padding: 8px 12px 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--color-text-muted);
  letter-spacing: 0.5px;
  background: var(--color-bg-tertiary);
  position: sticky;
  top: 0;
}

.ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.9rem;
}

.ref-item:hover,
.ref-item.active {
  background: var(--color-row-hover);
}

.ref-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.ref-name {
  flex: 1;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ref-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  flex-shrink: 0;
}

.ref-badge.default {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}

.ref-pr-info {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}
</style>
